interface AlertMessage {
  type: 'success' | 'error'
  title: string
  message: string
}

const alertMessage = ref<AlertMessage>({
  type: 'success',
  title: '',
  message: '',
})

const isAlertShowing = ref(false)

const ALERT_TIMEOUT = 5000

export default function useAlert() {
  const alert = (message: AlertMessage) => {
    if (isAlertShowing.value) {
      // If there's already an alert, close it and show it later
      isAlertShowing.value = true

      setTimeout(() => alert(message), 1000)
      return
    }

    alertMessage.value = message

    isAlertShowing.value = true

    setTimeout(() => {
      isAlertShowing.value = false
    }, ALERT_TIMEOUT)
  }

  const alertSuccess = ({
    type = 'success',
    title = 'Success',
    message = 'Action completed.',
  }: Partial<AlertMessage>) => {
    alert({ message, title, type })
  }

  const alertError = ({
    error = 'We\'ve encountered an unknown error. Please contact support@redwishes.com.',
    title,
    type = 'error',
  }: {
    error?: unknown
    title?: string
    type?: 'success' | 'error'
  }) => {
    if (typeof error === 'string') {
      alert({ message: error, title: title || 'Error', type })
      return
    }

    if (error instanceof Error && 'data' in error) {
      const data = error.data as { message: string, statusMessage: string }

      alert({
        message: data.statusMessage || data.message,
        title: title || 'Error',
        type,
      })

      return
    }

    if (error instanceof Error) {
      alert({
        message: error.message,
        title: title || 'Error',
        type,
      })
    }
  }

  const closeAlert = () => {
    isAlertShowing.value = false
  }

  return {
    alert,
    alertSuccess,
    alertError,
    closeAlert,
    alertMessage,
    isAlertShowing,
  }
}
